import React from 'react';
import PropTypes from 'prop-types';
import { get, sumBy, values } from 'lodash';
import {
  Cushion,
  FlexContainer,
  LinearGradient,
  Rectangle,
  Space,
  Typography,
} from '@pitchero/react-ui';
import { connect } from 'react-redux';
import Icon from '../icon';
import NotificationBadge from '../notification-badge';
import clubUrl from '../../lib/routing/club-url';
import { Trans } from '../../lib/i18n';
import { getCurrentClub } from '../../store/clubs/selectors';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import { appDomain } from '../../lib/env';
import WebsiteList from './website-list';

const MainNetworkPanelMenu = ({
  adminCount,
  clubs,
  leagueWebsites,
  badgeTotals,
  onClickAccount,
  onClickAdminDashboard,
  settingsUrl,
  currentClub,
}) => {
  const IconSize = 18;
  const clubCount = clubs.length;
  const accountBadgeTotal = sumBy(
    values(get(badgeTotals, 'clubs', [])),
    (club) => club.membership + club.messages + club.payments + club.schedule,
  );

  const logoutUrl = () =>
    `https://${
      currentClub.externalDomain
        ? currentClub.externalDomain
        : `${appDomain()}/clubs/${currentClub.folder}`
    }/logout?ref=${clubUrl(currentClub, 'homepage')}`;

  return (
    <LinearGradient
      component="div"
      angle={90}
      stops={[
        [0, 'alabaster'],
        [100, 'white'],
      ]}
      style={{ flex: 1, overflowY: 'auto' }}
    >
      <Rectangle fill="white" style={{ flexShrink: 0 }}>
        <Cushion vertical="medium" horizontal="xlarge">
          {clubCount > 0 && (
            <Space bottom="xsmall">
              <Cushion vertical="small" style={{ cursor: 'pointer' }} onClick={onClickAccount}>
                <FlexContainer justifyContent="space-between" alignItems="center">
                  <Space right="medium">
                    <Icon name="user" size={IconSize} color="dustygrey" />
                  </Space>
                  <Typography preset="subtitle--small" style={{ flex: 1 }} color="dustygrey">
                    <Trans i18nKey="common:network_panel_account_link">Your account</Trans>
                  </Typography>
                  <Space right="xsmall">
                    <NotificationBadge total={accountBadgeTotal} />
                  </Space>
                  {clubCount > 2 && <Icon name="arrow-right" size={14} color="dustygrey" />}
                </FlexContainer>
              </Cushion>
            </Space>
          )}
          {adminCount > 0 && (
            <Space bottom="xsmall">
              <Cushion
                vertical="small"
                style={{ cursor: 'pointer' }}
                onClick={onClickAdminDashboard}
              >
                <FlexContainer justifyContent="space-between" alignItems="center">
                  <Space right="medium">
                    <Icon name="spanner" size={IconSize} color="dustygrey" />
                  </Space>
                  <Typography preset="subtitle--small" style={{ flex: 1 }} color="dustygrey">
                    <Trans i18nKey="common:network_panel_admin_link">Admin</Trans>
                  </Typography>
                  {adminCount > 1 && <Icon name="arrow-right" size={14} color="dustygrey" />}
                </FlexContainer>
              </Cushion>
            </Space>
          )}
          <Space bottom="xsmall">
            <a
              href={`/token-redirect?url=${encodeURIComponent(settingsUrl)}`}
              style={{ display: 'block' }}
            >
              <Cushion vertical="small">
                <FlexContainer justifyContent="space-between" alignItems="center">
                  <Space right="medium">
                    <Icon name="cog" size={IconSize} color="dustygrey" />
                  </Space>
                  <Typography preset="subtitle--small" style={{ flex: 1 }} color="dustygrey">
                    <Trans i18nKey="common:network_panel_settings_link">Settings</Trans>
                  </Typography>
                </FlexContainer>
              </Cushion>
            </a>
          </Space>
          <Space bottom="xsmall">
            <a href="https://help.pitchero.com" style={{ display: 'block' }}>
              <Cushion vertical="small">
                <FlexContainer justifyContent="space-between" alignItems="center">
                  <Space right="medium">
                    <Icon name="help-bubble" size={IconSize} color="dustygrey" />
                  </Space>
                  <Typography preset="subtitle--small" color="dustygrey" style={{ flex: 1 }}>
                    <Trans i18nKey="common:network_panel_help_centre_link">Help Centre</Trans>
                  </Typography>
                </FlexContainer>
              </Cushion>
            </a>
          </Space>
          <a href={`${logoutUrl()}`} className="js-logout-action">
            <Cushion vertical="small">
              <FlexContainer justifyContent="space-between" alignItems="center">
                <Space right="medium">
                  <Icon name="exit" size={IconSize} color="#e85d61" />
                </Space>
                <Typography preset="subtitle--small" color="#e85d61" style={{ flex: 1 }}>
                  <Trans i18nKey="common:logout">Logout</Trans>
                </Typography>
              </FlexContainer>
            </Cushion>
          </a>
        </Cushion>
      </Rectangle>
      {clubCount > 0 && (
        <Cushion vertical="large" horizontal="xlarge" component="div">
          <WebsiteList clubs={clubs} leagueWebsites={leagueWebsites} />
        </Cushion>
      )}
    </LinearGradient>
  );
};

MainNetworkPanelMenu.propTypes = {
  adminCount: PropTypes.number.isRequired,
  currentClub: CLUB_PROP_TYPES.isRequired,
  clubs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      url: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
  leagueWebsites: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      url: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
  badgeTotals: PropTypes.shape({}).isRequired,
  onClickAccount: PropTypes.func.isRequired,
  onClickAdminDashboard: PropTypes.func.isRequired,
  settingsUrl: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
  currentClub: getCurrentClub(state),
});

export default connect(mapStateToProps)(MainNetworkPanelMenu);
